import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { 
  Users, Loader2, Save, Shield, Zap, Package, 
  LayoutGrid, Sparkles, Building2, Link2, BookOpen,
  Eye, EyeOff, ChevronDown, ChevronUp, RefreshCw, DollarSign, TrendingUp, Percent
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import api from '../../services/api';

const OVERRIDE_CATEGORIES = {
  product_tabs: { label: 'Product Tabs', icon: LayoutGrid, description: 'Tabs visible inside the product experience' },
  copilots: { label: 'AI Co-Pilots', icon: Sparkles, description: 'Which AI co-pilots are available' },
  org_nav: { label: 'Navigation & Org', icon: Building2, description: 'Sidebar and org-level features' },
  integrations: { label: 'Integrations', icon: Link2, description: 'Product integration connections' },
  ideation_tools: { label: 'Ideation Tools', icon: Zap, description: 'Ideation and research tools' },
  help: { label: 'Help & Docs', icon: BookOpen, description: 'Documentation and help access' },
};

function categorizeOverrides(overrides) {
  const categorized = {};
  for (const [flagId, status] of Object.entries(overrides)) {
    let cat = 'other';
    if (flagId.startsWith('tab_')) cat = 'product_tabs';
    else if (flagId.startsWith('copilot_')) cat = 'copilots';
    else if (flagId.startsWith('org_')) cat = 'org_nav';
    else if (flagId.startsWith('integration_')) cat = 'integrations';
    else if (flagId.startsWith('ideation_')) cat = 'ideation_tools';
    else if (flagId.startsWith('help_')) cat = 'help';
    if (!categorized[cat]) categorized[cat] = {};
    categorized[cat][flagId] = status;
  }
  return categorized;
}

function flagIdToLabel(flagId) {
  return flagId
    .replace(/^(tab_|copilot_|org_|integration_|ideation_|help_)/, '')
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const FreeTierManagementTab = ({ colors }) => {
  const [plan, setPlan] = useState(null);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingLimits, setSavingLimits] = useState(false);
  const [savingFlags, setSavingFlags] = useState(false);
  const [restrictions, setRestrictions] = useState({});
  const [overrides, setOverrides] = useState({});
  const [expandedCats, setExpandedCats] = useState({ product_tabs: true, copilots: false, org_nav: true, integrations: true, ideation_tools: false, help: false });
  const [dirty, setDirty] = useState({ limits: false, flags: false });

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/admin/feature-flags/free-tier');
      setPlan(res.data.plan);
      setStats(res.data.stats || {});
      setRestrictions(res.data.plan?.restrictions || {});
      setOverrides(res.data.plan?.feature_flag_overrides || {});
      setDirty({ limits: false, flags: false });
    } catch (err) {
      toast.error('Failed to load free tier config');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const updateRestriction = (key, value) => {
    setRestrictions(prev => ({ ...prev, [key]: value }));
    setDirty(prev => ({ ...prev, limits: true }));
  };

  const toggleOverride = (flagId) => {
    setOverrides(prev => ({
      ...prev,
      [flagId]: prev[flagId] === 'on' ? 'off' : 'on',
    }));
    setDirty(prev => ({ ...prev, flags: true }));
  };

  const saveLimits = async () => {
    try {
      setSavingLimits(true);
      await api.put('/api/admin/feature-flags/free-tier/restrictions', { restrictions });
      toast.success('Limits updated');
      setDirty(prev => ({ ...prev, limits: false }));
    } catch (err) {
      toast.error('Failed to save limits');
    } finally {
      setSavingLimits(false);
    }
  };

  const saveFlags = async () => {
    try {
      setSavingFlags(true);
      await api.put('/api/admin/feature-flags/free-tier/feature-flags', { feature_flag_overrides: overrides });
      toast.success('Feature flags updated');
      setDirty(prev => ({ ...prev, flags: false }));
    } catch (err) {
      toast.error('Failed to save feature flags');
    } finally {
      setSavingFlags(false);
    }
  };

  const toggleCategory = (cat) => {
    setExpandedCats(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className={cn("w-8 h-8 animate-spin", colors.textMuted)} />
      </div>
    );
  }

  if (!plan) {
    return (
      <Card className={cn(colors.bgCard, colors.borderPrimary)}>
        <CardContent className="py-12 text-center">
          <Shield className={cn("w-12 h-12 mx-auto mb-3", colors.textMuted)} />
          <p className={cn("text-lg font-medium", colors.textPrimary)}>Free Explorer Plan Not Found</p>
          <p className={cn("text-sm mt-1", colors.textMuted)}>The system plan has not been seeded. Restart the backend to seed it.</p>
        </CardContent>
      </Card>
    );
  }

  const categorized = categorizeOverrides(overrides);
  const onCount = Object.values(overrides).filter(v => v === 'on').length;
  const offCount = Object.values(overrides).filter(v => v === 'off').length;

  const LIMIT_FIELDS = [
    { key: 'max_products', label: 'Max Products', type: 'number', icon: Package },
    { key: 'max_epics', label: 'Max Epics', type: 'number', icon: LayoutGrid },
    { key: 'max_features', label: 'Max Features', type: 'number', icon: Zap },
    { key: 'max_paid_seats', label: 'Max Seats', type: 'number', icon: Users },
    { key: 'ai_tokens_per_month', label: 'AI Tokens / Month', type: 'number', icon: Sparkles },
    { key: 'max_collaboration_spaces', label: 'Collab Spaces', type: 'number', icon: Building2 },
    { key: 'max_deep_research_reports', label: 'Research Reports', type: 'number', icon: BookOpen },
  ];

  return (
    <div className="space-y-6" data-testid="free-tier-management-tab">
      {/* Header Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className={cn(colors.bgCard, colors.borderPrimary)}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <Users className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className={cn("text-xs", colors.textMuted)}>Free Tier Orgs</p>
                <p className={cn("text-2xl font-bold", colors.textPrimary)} data-testid="free-tier-org-count">
                  {stats.total_free_orgs || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={cn(colors.bgCard, colors.borderPrimary)}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Eye className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className={cn("text-xs", colors.textMuted)}>Features Enabled</p>
                <p className={cn("text-2xl font-bold", colors.textPrimary)}>{onCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={cn(colors.bgCard, colors.borderPrimary)}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/10">
                <EyeOff className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className={cn("text-xs", colors.textMuted)}>Features Disabled</p>
                <p className={cn("text-2xl font-bold", colors.textPrimary)}>{offCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={cn(colors.bgCard, colors.borderPrimary)}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Sparkles className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className={cn("text-xs", colors.textMuted)}>AI Token Budget</p>
                <p className={cn("text-2xl font-bold", colors.textPrimary)}>
                  {(restrictions.ai_tokens_per_month || 0).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* LEFT: Limits / Restrictions */}
        <Card className={cn(colors.bgCard, colors.borderPrimary)}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className={cn("text-base", colors.textPrimary)}>Plan Limits</CardTitle>
                <p className={cn("text-xs mt-1", colors.textMuted)}>Resource caps for free tier accounts</p>
              </div>
              <Button
                size="sm"
                onClick={saveLimits}
                disabled={!dirty.limits || savingLimits}
                className={cn(!dirty.limits && "opacity-50")}
                data-testid="save-limits-btn"
              >
                {savingLimits ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                Save Limits
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {LIMIT_FIELDS.map(({ key, label, icon: Icon }) => (
              <div key={key} className={cn("flex items-center justify-between p-3 rounded-lg border", colors.bgSecondary, colors.borderPrimary)}>
                <div className="flex items-center gap-3">
                  <Icon className={cn("w-4 h-4", colors.textMuted)} />
                  <span className={cn("text-sm font-medium", colors.textPrimary)}>{label}</span>
                </div>
                <Input
                  type="number"
                  min={0}
                  value={restrictions[key] ?? 0}
                  onChange={(e) => updateRestriction(key, parseInt(e.target.value) || 0)}
                  className={cn("w-28 h-8 text-right text-sm", colors.bgInput, colors.borderPrimary)}
                  data-testid={`limit-${key}`}
                />
              </div>
            ))}

            {/* Boolean toggles */}
            <div className={cn("flex items-center justify-between p-3 rounded-lg border", colors.bgSecondary, colors.borderPrimary)}>
              <div className="flex items-center gap-3">
                <Sparkles className={cn("w-4 h-4", colors.textMuted)} />
                <span className={cn("text-sm font-medium", colors.textPrimary)}>AI Access</span>
              </div>
              <Switch
                checked={restrictions.ai_access ?? true}
                onCheckedChange={(v) => updateRestriction('ai_access', v)}
                data-testid="limit-ai-access"
              />
            </div>
            <div className={cn("flex items-center justify-between p-3 rounded-lg border", colors.bgSecondary, colors.borderPrimary)}>
              <div className="flex items-center gap-3">
                <Shield className={cn("w-4 h-4", colors.textMuted)} />
                <span className={cn("text-sm font-medium", colors.textPrimary)}>Priority Support</span>
              </div>
              <Switch
                checked={restrictions.priority_support ?? false}
                onCheckedChange={(v) => updateRestriction('priority_support', v)}
                data-testid="limit-priority-support"
              />
            </div>
          </CardContent>
        </Card>

        {/* RIGHT COLUMN */}
        <div className="space-y-6">
          {/* Token Pricing Markup */}
          <Card className={cn(colors.bgCard, "border-amber-500/30")} data-testid="token-pricing-markup-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-md bg-amber-500/10">
                    <TrendingUp className="w-4 h-4 text-amber-400" />
                  </div>
                  <div>
                    <CardTitle className={cn("text-base", colors.textPrimary)}>Token Pricing Markup</CardTitle>
                    <p className={cn("text-xs mt-0.5", colors.textMuted)}>Free tier users are charged a premium on token consumption</p>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className={cn("flex items-center justify-between p-3 rounded-lg border", colors.bgSecondary, colors.borderPrimary)}>
                <div className="flex items-center gap-3">
                  <Percent className={cn("w-4 h-4 text-amber-400")} />
                  <div>
                    <span className={cn("text-sm font-medium", colors.textPrimary)}>Markup Percentage</span>
                    <p className={cn("text-xs", colors.textMuted)}>Applied on top of base token cost</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    max={500}
                    value={restrictions.token_cost_markup_percent ?? 0}
                    onChange={(e) => updateRestriction('token_cost_markup_percent', parseInt(e.target.value) || 0)}
                    className={cn("w-24 h-8 text-right text-sm", colors.bgInput, colors.borderPrimary)}
                    data-testid="limit-token-cost-markup-percent"
                  />
                  <span className={cn("text-sm font-medium", colors.textMuted)}>%</span>
                </div>
              </div>
              {/* Effective rate preview */}
              <div className={cn("p-3 rounded-lg border", "bg-amber-500/5 border-amber-500/20")}>
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-3.5 h-3.5 text-amber-400" />
                  <span className={cn("text-xs font-medium uppercase tracking-wider text-amber-400")}>Effective Rate</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className={cn(colors.textMuted)}>Multiplier</div>
                  <div className={cn("text-right font-mono font-semibold", colors.textPrimary)} data-testid="markup-multiplier">
                    {(1 + (restrictions.token_cost_markup_percent || 0) / 100).toFixed(2)}x
                  </div>
                  <div className={cn(colors.textMuted)}>Example: 100K tokens</div>
                  <div className={cn("text-right font-mono", colors.textPrimary)} data-testid="markup-example">
                    Debits {((100000 * (1 + (restrictions.token_cost_markup_percent || 0) / 100))).toLocaleString(undefined, {maximumFractionDigits: 0})} from budget
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* What they see (Preview) */}
          <Card className={cn(colors.bgCard, colors.borderPrimary)}>
            <CardHeader className="pb-3">
              <CardTitle className={cn("text-base", colors.textPrimary)}>Free Explorer Experience</CardTitle>
              <p className={cn("text-xs", colors.textMuted)}>Preview of what a free tier user sees</p>
            </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className={cn("text-xs font-medium mb-2 uppercase tracking-wider", colors.textMuted)}>Product Tabs</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(overrides)
                  .filter(([k]) => k.startsWith('tab_'))
                  .map(([flagId, status]) => (
                    <Badge
                      key={flagId}
                      variant="outline"
                      className={cn(
                        "text-xs",
                        status === 'on'
                          ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"
                          : "bg-zinc-500/10 text-zinc-500 border-zinc-500/20 line-through"
                      )}
                    >
                      {flagIdToLabel(flagId)}
                    </Badge>
                  ))}
              </div>
            </div>
            <div>
              <p className={cn("text-xs font-medium mb-2 uppercase tracking-wider", colors.textMuted)}>Active Copilots</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(overrides)
                  .filter(([k, v]) => k.startsWith('copilot_') && v === 'on')
                  .map(([flagId]) => (
                    <Badge key={flagId} variant="outline" className="text-xs bg-blue-500/10 text-blue-400 border-blue-500/30">
                      {flagIdToLabel(flagId)}
                    </Badge>
                  ))}
              </div>
            </div>
            <div>
              <p className={cn("text-xs font-medium mb-2 uppercase tracking-wider", colors.textMuted)}>Integrations</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(overrides)
                  .filter(([k]) => k.startsWith('integration_'))
                  .map(([flagId, status]) => (
                    <Badge
                      key={flagId}
                      variant="outline"
                      className={cn(
                        "text-xs",
                        status === 'on'
                          ? "bg-violet-500/10 text-violet-400 border-violet-500/30"
                          : "bg-zinc-500/10 text-zinc-500 border-zinc-500/20 line-through"
                      )}
                    >
                      {flagIdToLabel(flagId)}
                    </Badge>
                  ))}
              </div>
            </div>
            <div className={cn("p-3 rounded-lg border mt-2", colors.bgSecondary, colors.borderPrimary)}>
              <p className={cn("text-xs font-medium mb-2 uppercase tracking-wider", colors.textMuted)}>Limits Summary</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className={cn(colors.textMuted)}>Products</div>
                <div className={cn("text-right font-mono", colors.textPrimary)}>{restrictions.max_products}</div>
                <div className={cn(colors.textMuted)}>Epics</div>
                <div className={cn("text-right font-mono", colors.textPrimary)}>{restrictions.max_epics}</div>
                <div className={cn(colors.textMuted)}>Features</div>
                <div className={cn("text-right font-mono", colors.textPrimary)}>{restrictions.max_features}</div>
                <div className={cn(colors.textMuted)}>AI Tokens/mo</div>
                <div className={cn("text-right font-mono", colors.textPrimary)}>{(restrictions.ai_tokens_per_month || 0).toLocaleString()}</div>
                <div className={cn(colors.textMuted)}>Seats</div>
                <div className={cn("text-right font-mono", colors.textPrimary)}>{restrictions.max_paid_seats}</div>
                <div className={cn(colors.textMuted)}>Token Markup</div>
                <div className={cn("text-right font-mono text-amber-400")}>{restrictions.token_cost_markup_percent || 0}%</div>
              </div>
            </div>
            <div>
              <p className={cn("text-xs font-medium mb-1 uppercase tracking-wider", colors.textMuted)}>Suppressed</p>
              <p className={cn("text-xs", colors.textMuted)}>
                No Sidebar, No Dashboard, No Team, No Executive AI, No Visualizations, No Goals/Initiatives/Personas
              </p>
            </div>
          </CardContent>
          </Card>
        </div>
      </div>

      {/* Feature Flag Overrides — Full Width */}
      <Card className={cn(colors.bgCard, colors.borderPrimary)}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className={cn("text-base", colors.textPrimary)}>Feature Flag Overrides</CardTitle>
              <p className={cn("text-xs mt-1", colors.textMuted)}>
                These override global feature flags for all free tier accounts. Changes require save.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={loadConfig} className={cn(colors.borderPrimary)}>
                <RefreshCw className="w-3 h-3 mr-1" /> Reload
              </Button>
              <Button
                size="sm"
                onClick={saveFlags}
                disabled={!dirty.flags || savingFlags}
                className={cn(!dirty.flags && "opacity-50")}
                data-testid="save-flags-btn"
              >
                {savingFlags ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />}
                Save Flags
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {Object.entries(OVERRIDE_CATEGORIES).map(([catKey, catConfig]) => {
            const catFlags = categorized[catKey] || {};
            const flagEntries = Object.entries(catFlags);
            if (flagEntries.length === 0) return null;
            const CatIcon = catConfig.icon;
            const isExpanded = expandedCats[catKey];
            const catOnCount = flagEntries.filter(([, v]) => v === 'on').length;

            return (
              <div key={catKey} className={cn("rounded-lg border", colors.borderPrimary)}>
                <button
                  onClick={() => toggleCategory(catKey)}
                  className={cn(
                    "w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors rounded-lg",
                    colors.textPrimary
                  )}
                  data-testid={`category-toggle-${catKey}`}
                >
                  <div className="flex items-center gap-3">
                    <CatIcon className={cn("w-4 h-4", colors.textMuted)} />
                    <span className="text-sm font-medium">{catConfig.label}</span>
                    <Badge variant="outline" className="text-[10px]">
                      {catOnCount}/{flagEntries.length} on
                    </Badge>
                  </div>
                  {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>

                {isExpanded && (
                  <div className="px-3 pb-3 space-y-1">
                    {flagEntries.map(([flagId, status]) => (
                      <div
                        key={flagId}
                        className={cn(
                          "flex items-center justify-between py-2 px-3 rounded-md",
                          colors.bgSecondary
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <div className={cn(
                            "w-2 h-2 rounded-full",
                            status === 'on' ? "bg-emerald-400" : "bg-zinc-600"
                          )} />
                          <span className={cn("text-sm", colors.textPrimary)}>{flagIdToLabel(flagId)}</span>
                          <span className={cn("text-xs font-mono", colors.textMuted)}>{flagId}</span>
                        </div>
                        <Switch
                          checked={status === 'on'}
                          onCheckedChange={() => toggleOverride(flagId)}
                          data-testid={`flag-toggle-${flagId}`}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
};

export default FreeTierManagementTab;
